import {
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
} from '@chakra-ui/react'
import { analytics } from 'analytics'
import { isEmpty, isNumber } from 'lodash'
import { useEffect, useMemo, useState } from 'react'

import { HttpStatus } from 'enums'
import { Portfolio, PortfolioLinkType } from 'types'

import { paveApi } from 'api'
import ModalLoader from 'components/common/Loader/ModalLoader'
import PageLoader from 'components/common/Loader/PageLoader'
import PortfolioConfirmTradesModal from 'components/partials/Portfolio/V2/Modals/PortfolioConfirmTradesModal'
import { API_TAG } from 'constants/app.constants'
import {
  PORTFOLIO_APPROVE_TRADES_AUTOMATION_SUBMITTED,
  PORTFOLIO_APPROVE_TRADES_AUTOMATION_SUBMITTED_FAILED,
  PORTFOLIO_APPROVE_TRADES_AUTOMATION_SUBMITTED_SUCCESS,
  PORTFOLIO_APPROVE_TRADES_CONFIRMATION_MODAL_CLOSED,
  PORTFOLIO_APPROVE_TRADES_CONFIRMATION_MODAL_OPENED,
  PORTFOLIO_APPROVE_TRADES_MANUAL_SUBMITTED,
  PORTFOLIO_APPROVE_TRADES_MANUAL_SUBMITTED_FAILED,
  PORTFOLIO_APPROVE_TRADES_MANUAL_SUBMITTED_SUCCESS,
} from 'constants/track.constants'
import PDSBox from 'designSystem/components/PDSBox/PDSBox'
import PDSButton from 'designSystem/components/PDSButton/PDSButton'
import PDSFlexBox from 'designSystem/components/PDSFlexBox/PDSFlexBox'
import PDSHeader from 'designSystem/components/PDSHeader/PDSHeader'
import PDSIconButton from 'designSystem/components/PDSIconButton/PDSIconButton'
import PDSSeparator from 'designSystem/components/PDSSeparator/PDSSeparator'
import PDSText from 'designSystem/components/PDSText/PDSText'
import { COLOR } from 'designSystem/foundations/constants/color'
import { FONT_SIZE } from 'designSystem/foundations/constants/fontSize'
import { SIZE } from 'designSystem/foundations/constants/size'
import { useCustomToast } from 'hooks/useCustomToast'
import { formatAsCurrency } from 'utils'
import {
  getPortfolioLinkType,
  isAutomationSupportedPortfolio,
  processSuggestedTrades,
} from 'utils/portfolioUtil'

import PortfolioSuggestedTradesList from './PortfolioSuggestedTradesList'

type Props = {
  onClose: () => void
  originalPortfolio: Portfolio
  showSlideIn: boolean
}

export default function PortfolioViewSuggestedTradesSlideIn({
  onClose,
  originalPortfolio,
  showSlideIn,
}: Props) {
  const [showConfirmTradesModal, setShowConfirmTradesModal] = useState(false)
  const [showLoader, setShowLoader] = useState(false)
  const { showErrorToast, showSuccessToast, showViewOnlyDefaultToast } =
    useCustomToast()

  const [emailSuggestedTrades] = paveApi.useEmailSuggestedTradesMutation()
  const [patchPortfolio] = paveApi.usePatchPortfolioMutation()
  const [
    updateSuggestedTrades,
    { data: updatedPortfolio, isLoading: isUpdateLoading },
  ] = paveApi.useUpdateSuggestedTradesMutation()
  const { data: assets } = paveApi.useGetAssetsQuery()

  const { id: portfolioId } = originalPortfolio ?? {}
  const { investmentPreference, suggestedTrades, totalRealizedGain } =
    updatedPortfolio ?? {}

  const isAutomationSupported = isAutomationSupportedPortfolio(updatedPortfolio)
  const linkType = getPortfolioLinkType(updatedPortfolio)

  const fullTrades = useMemo(() => {
    return processSuggestedTrades(suggestedTrades ?? [], assets)
  }, [assets, suggestedTrades])

  const { otherAssetTrades, closeAssetTrades, newAssetTrades } = fullTrades

  useEffect(() => {
    if (showSlideIn && portfolioId) {
      updateSuggestedTrades(portfolioId)
    }
  }, [portfolioId, showSlideIn, updateSuggestedTrades])

  const handleExecuteTrades = () => {
    let payload: Partial<Portfolio> = {}
    if (isAutomationSupported) {
      payload = {
        needsTradeExecution: true,
        userApprovedAutomatedTradesOnce: !!investmentPreference?.automation,
      }
    } else if (linkType === PortfolioLinkType.Plaid) {
      payload = {
        suggestedTrades: [],
        lastTradedAt: new Date().toISOString(),
      }
    } else {
      payload = {
        suggestedTrades: [],
      }
    }

    const automation = investmentPreference?.automation === true

    const SUBMITTED_EVENT = automation
      ? PORTFOLIO_APPROVE_TRADES_AUTOMATION_SUBMITTED
      : PORTFOLIO_APPROVE_TRADES_MANUAL_SUBMITTED
    const SUCCESS_EVENT = automation
      ? PORTFOLIO_APPROVE_TRADES_AUTOMATION_SUBMITTED_SUCCESS
      : PORTFOLIO_APPROVE_TRADES_MANUAL_SUBMITTED_SUCCESS
    const FAILED_EVENT = automation
      ? PORTFOLIO_APPROVE_TRADES_AUTOMATION_SUBMITTED_FAILED
      : PORTFOLIO_APPROVE_TRADES_MANUAL_SUBMITTED_FAILED

    analytics.track(SUBMITTED_EVENT, {
      type: linkType,
    })

    setShowConfirmTradesModal(false)
    setShowLoader(true)

    patchPortfolio({
      body: {
        id: portfolioId,
        ...payload,
      },
      invalidatesTags: [API_TAG.PORTFOLIO_STATS],
    })
      .unwrap()
      .then(() => {
        analytics.track(SUCCESS_EVENT, {
          type: linkType,
        })
        showSuccessToast('Portfolio updated!')
        onClose()
      })
      .catch((e) => {
        analytics.track(FAILED_EVENT, {
          type: linkType,
        })
        if (e?.status === HttpStatus.FORBIDDEN) {
          showViewOnlyDefaultToast()
        } else {
          showErrorToast('An error occurred updating the portfolio.')
        }
      })
      .finally(() => {
        setShowLoader(false)
      })
  }

  const handleEmailTrades = () => {
    setShowLoader(true)
    emailSuggestedTrades({ id: portfolioId })
      .unwrap()
      .then(() => {
        showSuccessToast('Email sent!')
      })
      .catch((e) => {
        if (e?.status === HttpStatus.FORBIDDEN) {
          showViewOnlyDefaultToast()
        } else {
          showErrorToast(
            'An error occurred when emailing the suggested trades.'
          )
        }
      })
      .finally(() => {
        setShowLoader(false)
      })
  }

  const closeConfirmationModal = () => {
    analytics.track(PORTFOLIO_APPROVE_TRADES_CONFIRMATION_MODAL_CLOSED)
    setShowConfirmTradesModal(false)
  }

  const openConfirmationModal = () => {
    analytics.track(PORTFOLIO_APPROVE_TRADES_CONFIRMATION_MODAL_OPENED)
    setShowConfirmTradesModal(true)
  }

  return (
    <>
      <Drawer
        isOpen={showSlideIn}
        onClose={onClose}
        placement="right"
        size="lg"
        trapFocus={true}
      >
        <DrawerOverlay />
        <DrawerContent>
          <DrawerHeader>
            <PDSFlexBox alignItems="center" justifyContent="space-between">
              <PDSHeader color={COLOR.textSecondary} variant="h4">
                Suggested Trades
              </PDSHeader>
              <PDSIconButton
                name="close"
                onClick={onClose}
                testId="close-suggested-trades-slide-in"
              />
            </PDSFlexBox>
          </DrawerHeader>
          <PDSSeparator />

          <DrawerBody paddingTop={SIZE.s40} paddingX={SIZE.s32}>
            {isUpdateLoading ? (
              <PageLoader />
            ) : (
              <>
                {linkType !== PortfolioLinkType.None &&
                  isNumber(totalRealizedGain) && (
                    <PDSBox>
                      <PDSBox>
                        <PDSHeader variant="h3">
                          Estimated Realized Gains
                        </PDSHeader>
                      </PDSBox>
                      <PDSBox marginTop={SIZE.s8}>
                        <PDSText
                          color={COLOR.textPrimary}
                          fontSize={FONT_SIZE.fs22}
                          weight="medium"
                        >
                          {formatAsCurrency(totalRealizedGain)}
                        </PDSText>
                      </PDSBox>
                    </PDSBox>
                  )}

                <PDSBox marginTop={SIZE.s20}>
                  <PDSHeader variant="h3">Positions</PDSHeader>
                  <PDSSeparator marginTop={SIZE.s8} />
                </PDSBox>

                {newAssetTrades?.length > 0 && (
                  <PortfolioSuggestedTradesList
                    portfolioId={portfolioId}
                    suggestedTrades={newAssetTrades}
                    title="New Positions"
                  />
                )}
                {closeAssetTrades?.length > 0 && (
                  <PortfolioSuggestedTradesList
                    portfolioId={portfolioId}
                    suggestedTrades={closeAssetTrades}
                    title="Closed Positions"
                  />
                )}
                {otherAssetTrades?.length > 0 && (
                  <PortfolioSuggestedTradesList
                    portfolioId={portfolioId}
                    suggestedTrades={otherAssetTrades}
                    title="Changed Positions"
                  />
                )}
              </>
            )}
          </DrawerBody>

          <PDSSeparator />
          <DrawerFooter flexDirection="column" padding={SIZE.s32}>
            <PDSFlexBox width="100%" gap={SIZE.s16}>
              <PDSButton
                disabled={isEmpty(suggestedTrades) || isUpdateLoading}
                onClick={openConfirmationModal}
                size="lg-f"
                testId="approve-trades"
                text={
                  isAutomationSupported ? 'Trade Now' : 'Mark Trades Complete'
                }
              />
              <PDSButton
                disabled={isEmpty(suggestedTrades) || isUpdateLoading}
                iconLeft="mail"
                onClick={handleEmailTrades}
                size="lg-f"
                testId="email-trades"
                text="Email Trades"
                variant="tertiary"
              />
            </PDSFlexBox>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
      <PortfolioConfirmTradesModal
        isAutomationSupported={isAutomationSupported}
        onConfirm={handleExecuteTrades}
        onClose={closeConfirmationModal}
        showModal={showConfirmTradesModal}
      />
      {showLoader && <ModalLoader />}
    </>
  )
}
